class LifecycleManager {
  constructor() {
    this.listeners = [];
    this.observers = [];
    this.intervals = [];
    this.timeouts = [];
    this.cleanupFunctions = [];
    this.isDestroyed = false;
  }
  /**
   * Set the element this lifecycle manager is managing
   * @param element - The HTML element
   */
  setElement(element) {
    this.element = element;
  }
  /**
   * Track an event listener for later cleanup
   * @param event - Event name
   * @param handler - Event handler function
   * @param options - Event listener options
   */
  trackListener(event, handler, options) {
    this.assertNotDestroyed();
    this.listeners.push({ event, handler, options });
  }
  /**
   * Track a mutation observer for later cleanup
   * @param observer - MutationObserver instance
   * @param target - The node being observed
   * @param config - Observer configuration
   */
  trackObserver(observer, target, config) {
    this.assertNotDestroyed();
    this.observers.push({ observer, target, config });
  }
  /**
   * Track an interval for later cleanup
   * @param id - Interval ID from setInterval
   */
  trackInterval(id) {
    this.assertNotDestroyed();
    this.intervals.push(id);
  }
  /**
   * Track a timeout for later cleanup
   * @param id - Timeout ID from setTimeout
   */
  trackTimeout(id) {
    this.assertNotDestroyed();
    this.timeouts.push(id);
  }
  /**
   * Track a custom cleanup function
   * @param fn - Function to call during cleanup
   */
  trackCleanup(fn) {
    this.assertNotDestroyed();
    this.cleanupFunctions.push(fn);
  }
  /**
   * Create and track a new interval
   * @param callback - Function to call on interval
   * @param ms - Milliseconds between calls
   * @returns Interval ID
   */
  createInterval(callback, ms) {
    this.assertNotDestroyed();
    const id = window.setInterval(callback, ms);
    this.trackInterval(id);
    return id;
  }
  /**
   * Create and track a new timeout
   * @param callback - Function to call after timeout
   * @param ms - Milliseconds to wait
   * @returns Timeout ID
   */
  createTimeout(callback, ms) {
    this.assertNotDestroyed();
    const id = window.setTimeout(callback, ms);
    this.trackTimeout(id);
    return id;
  }
  /**
   * Create and track a new mutation observer
   * @param callback - Observer callback
   * @param target - Node to observe
   * @param options - Observer options
   * @returns MutationObserver instance
   */
  createObserver(callback, target, options) {
    this.assertNotDestroyed();
    const observer = new MutationObserver(callback);
    observer.observe(target, options);
    this.trackObserver(observer, target, options);
    return observer;
  }
  /**
   * Clean up all tracked resources
   * This is called automatically when the element is destroyed
   */
  cleanup() {
    if (this.isDestroyed) {
      return;
    }
    if (this.element) {
      this.listeners.forEach(({ event, handler, options }) => {
        this.element.removeEventListener(event, handler, options);
      });
    }
    this.observers.forEach(({ observer }) => {
      observer.disconnect();
    });
    this.intervals.forEach((id) => {
      clearInterval(id);
    });
    this.timeouts.forEach((id) => {
      clearTimeout(id);
    });
    this.cleanupFunctions.forEach((fn) => {
      try {
        fn();
      } catch (error) {
        console.error("Error in cleanup function:", error);
      }
    });
    this.listeners = [];
    this.observers = [];
    this.intervals = [];
    this.timeouts = [];
    this.cleanupFunctions = [];
    this.isDestroyed = true;
  }
  /**
   * Check if this lifecycle manager has been destroyed
   */
  isCleanedUp() {
    return this.isDestroyed;
  }
  /**
   * Get count of tracked resources (for debugging)
   */
  getResourceCounts() {
    return {
      listeners: this.listeners.length,
      observers: this.observers.length,
      intervals: this.intervals.length,
      timeouts: this.timeouts.length,
      cleanupFunctions: this.cleanupFunctions.length
    };
  }
  /**
   * Assert that the lifecycle manager hasn't been destroyed
   * @private
   */
  assertNotDestroyed() {
    if (this.isDestroyed) {
      throw new Error(
        "LifecycleManager: Cannot track resources after cleanup has been called"
      );
    }
  }
}
class GlobalLifecycleRegistry {
  constructor() {
    this.managers = /* @__PURE__ */ new WeakMap();
    this.allManagers = /* @__PURE__ */ new Set();
  }
  static getInstance() {
    if (!GlobalLifecycleRegistry.instance) {
      GlobalLifecycleRegistry.instance = new GlobalLifecycleRegistry();
    }
    return GlobalLifecycleRegistry.instance;
  }
  register(element, manager) {
    this.managers.set(element, manager);
    this.allManagers.add(manager);
  }
  get(element) {
    return this.managers.get(element);
  }
  cleanupAll() {
    this.allManagers.forEach((manager) => {
      if (!manager.isCleanedUp()) {
        manager.cleanup();
      }
    });
    this.allManagers.clear();
  }
  /**
   * Get statistics about all tracked lifecycle managers
   * Useful for debugging memory leaks
   */
  getStats() {
    const totalResources = {
      listeners: 0,
      observers: 0,
      intervals: 0,
      timeouts: 0,
      cleanupFunctions: 0
    };
    let active = 0;
    this.allManagers.forEach((manager) => {
      if (!manager.isCleanedUp()) {
        active++;
        const counts = manager.getResourceCounts();
        totalResources.listeners += counts.listeners;
        totalResources.observers += counts.observers;
        totalResources.intervals += counts.intervals;
        totalResources.timeouts += counts.timeouts;
        totalResources.cleanupFunctions += counts.cleanupFunctions;
      }
    });
    return {
      total: this.allManagers.size,
      active,
      totalResources
    };
  }
}
function getGlobalLifecycleRegistry() {
  return GlobalLifecycleRegistry.getInstance();
}
function cleanupAll() {
  GlobalLifecycleRegistry.getInstance().cleanupAll();
}
function getLifecycleStats() {
  return GlobalLifecycleRegistry.getInstance().getStats();
}
export {
  LifecycleManager,
  cleanupAll,
  getGlobalLifecycleRegistry,
  getLifecycleStats
};
//# sourceMappingURL=LifecycleManager.js.map
